import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import AddressInput from 'components/field/AddressInput';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Tag from 'components/global/Tag';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useMediaQuery from 'hooks/useMediaQuery';

import LengthUnitLotties from 'pages/Shipments/ActionForms/CreateShipment/Components/LengthUnitLotties';

import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import * as S from './PriceCalculatorWeb.styled';

const PriceCalculatorWeb = () => {
	const { t } = useTranslation();
	const [data, setData] = useState(null);
	const [init, setInit] = useState(false);
	const [searchParams] = useSearchParams();
	const changeLanguage = useChangeLanguage();
	const { showError } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const isFirstRequest = useRef(false);
	const containerRef = useRef(null);
	const [containerHeight, setContainerHeight] = useState(null);
	const [selectedPackage, setSelectedPackage] = useState('ScalePackage');
	const isMobileLandscape = useMediaQuery(Breakpoints.isMobileLandscape);

	const language = useMemo(
		() =>
			({
				ar: {
					systemCode: 'arabicValue',
					code: 'ar_SA'
				},
				en: { systemCode: 'engValue', code: 'en_US' },
				tr: { systemCode: 'trValue', code: 'tr_TR' }
			})[searchParams.get('lang') ?? 'en'],
		[searchParams]
	);

	useEffect(() => {
		changeLanguage(language.systemCode).then(() => setInit(true));
	}, [language]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				originAddress: yup
					.object()
					.shape({
						text: yup.string().nullable().required(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						countryName: yup.string().nullable().required()
					})
					.required(),
				destinationAddress: yup
					.object()
					.shape({
						text: yup.string().nullable().required(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						countryName: yup.string().nullable().required()
					})
					.required()
			}),
		[init]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			originAddress: {
				text: searchParams.get('origin') ?? null,
				cityName: null,
				districtName: null,
				countryName: null
			},
			destinationAddress: {
				text: searchParams.get('destination') ?? null,
				cityName: null,
				districtName: null,
				countryName: null
			},
			boxes: [{ width: 10, height: 10, length: 10, weight: 1 }]
		}
	});

	useEffect(() => {
		const interval = setInterval(() => {
			const tempHeight = containerRef.current?.clientHeight;
			if (tempHeight === containerHeight) return;

			setContainerHeight(tempHeight);
			window.parent.postMessage({ event: 'Resize', height: tempHeight }, '*');
		}, 500);

		return () => {
			clearInterval(interval);
		};
	}, [containerHeight]);

	const {
		watch,
		handleSubmit,
		setValue,
		setError,
		formState: { isSubmitted, isSubmitting }
	} = formMethods;

	const onSubmit = async (data) => {
		const cloneOfData = structuredClone(data);

		await axiosPrivate
			.post(PRICE_CONFIGURATIONS.GET_PRICE_LIST_OPEN, {
				calculationData: {
					boxes: data.boxes,
					originAddress: {
						city: data.originAddress.cityName,
						district: data.originAddress.districtName,
						country: data.originAddress.countryName
					},
					destinationAddress: {
						city: data.destinationAddress.cityName,
						district: data.destinationAddress.districtName,
						country: data.destinationAddress.countryName
					}
				}
			})
			.then((response) => {
				const { data: responseData, status } = response;
				if (status === 200) {
					if (responseData) {
						setData(responseData);
						setValue('resultOriginCity', cloneOfData.originAddress.cityName);
						setValue('resultDestinationCity', cloneOfData.destinationAddress.cityName);
					}
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});

		if (!isFirstRequest.current) isFirstRequest.current = true;
	};

	const boxColSpan = {
		md: 3
	};

	const itemColSpan = {
		md: 6
	};

	const originAddress = watch('originAddress');
	const destinationAddress = watch('destinationAddress');

	useEffect(() => {
		if (
			!isFirstRequest.current &&
			originAddress.cityName &&
			originAddress.countryName &&
			destinationAddress.cityName &&
			destinationAddress.countryName
		) {
			handleSubmit(onSubmit, (errors) => {
				AppLogger.log(errors);
				showError({
					message: 'errorMsg.cityAndCountryRequired'
				});
			})();
		}
	}, [originAddress, destinationAddress]);

	const getPackagePrice = (item) => {
		switch (selectedPackage) {
			case 'FreePackage':
				return item.price;
			case 'StarterPackage':
				return item.starterPrice ?? item.price;
			case 'ScalePackage':
				return item.scalePrice ?? item.price;
			default:
				return item.price;
		}
	};

	if (!init) return <Loading />;

	return (
		<S.Container ref={containerRef}>
			<Form formMethods={formMethods} borderless>
				<FormItem colSpan={itemColSpan} label="originCity" toolTip="priceCalculator.originTooltip" required>
					<AddressInput
						name="originAddress"
						showButton={false}
						showCountries={false}
						getPlaceOnFirstLoad={true}
						restrictions={{
							country: { code: ['SA', 'TR', 'AE'] }
						}}
						predictionFontSize="18px"
					/>
				</FormItem>
				<FormItem
					colSpan={itemColSpan}
					label="destinationCity"
					toolTip="priceCalculator.destinationTooltip"
					required>
					<AddressInput
						name="destinationAddress"
						showButton={false}
						showCountries={false}
						getPlaceOnFirstLoad={true}
						predictionFontSize="18px"
					/>
				</FormItem>
				{isSubmitted && (
					<>
						<FormItem
							label="weight"
							required
							col={12}
							colSpan={boxColSpan}
							toolTip="priceCalculator.weightTooltip">
							<IntegerInput min={0} unit="kg" name="boxes.0.weight" decimalLimit={1} allowFloat />
						</FormItem>
						<FormItem
							label="length"
							required
							col={12}
							colSpan={boxColSpan}
							toolTip="priceCalculator.lengthTooltip">
							<Tooltip title={<LengthUnitLotties unit="length" />} placement="top" color="var(--white)">
								<IntegerInput min={0} unit="cm" name="boxes.0.length" />
							</Tooltip>
						</FormItem>
						<FormItem
							label="width"
							required
							col={12}
							colSpan={boxColSpan}
							toolTip="priceCalculator.widthTooltip">
							<Tooltip title={<LengthUnitLotties unit="width" />} placement="top" color="var(--white)">
								<IntegerInput min={0} unit="cm" name="boxes.0.width" />
							</Tooltip>
						</FormItem>
						<FormItem
							label="height"
							required
							col={12}
							colSpan={boxColSpan}
							toolTip="priceCalculator.heightTooltip">
							<Tooltip title={<LengthUnitLotties unit="height" />} placement="top" color="var(--white)">
								<IntegerInput min={0} unit="cm" name="boxes.0.height" />
							</Tooltip>
						</FormItem>
					</>
				)}
				<S.ButtonContainer>
					<Button
						label="calculateShippingRates"
						onClick={handleSubmit(onSubmit, (errors) => {
							if (errors?.originAddress) {
								setError('originAddress.text', {
									message: t('errorMsg.cityAndCountryRequired'),
									type: 'required'
								});
							}

							if (errors?.destinationAddress) {
								setError('destinationAddress.text', {
									message: t('errorMsg.cityAndCountryRequired'),
									type: 'required'
								});
							}

							AppLogger.log(errors);
						})}
						loading={isSubmitting}
						size="xx-large"
					/>
				</S.ButtonContainer>
			</Form>
			{data?.length === 0 && (
				<NoRecordsFound
					title="priceCalculatorWeb.noRecordsFoundTitle"
					subTitle="priceCalculatorWeb.noRecordsFoundSubtitle"
				/>
			)}
			{data?.length > 0 && (
				<S.ResultContainer>
					<S.PackageTabsContainer>
						{['FreePackage', 'StarterPackage', 'ScalePackage'].map((item, index) => (
							<S.PackageTab
								key={index}
								onClick={() => setSelectedPackage(item)}
								className={`${item}-tab-title ${selectedPackage === item ? 'selected' : ''}`}>
								<Typography fontWeight="700" align="center" fullWidth>
									{item}
								</Typography>
							</S.PackageTab>
						))}
					</S.PackageTabsContainer>
					<Typography variant="h2" noWrap>
						<Trans
							i18nKey="priceCalculatorWeb.resultTitle"
							values={{
								originCity: watch('resultOriginCity'),
								destinationCity: watch('resultDestinationCity'),
								count: data?.length
							}}
						/>
					</Typography>
					<S.DataWrapper>
						{data?.map((item, index) => (
							<S.PriceCard key={index}>
								<S.LeftContentWrapper>
									<S.Logo
										url={item.logo}
										width={isMobileLandscape ? '70px' : '120px'}
										height={isMobileLandscape ? '70px' : '120px'}
									/>

									<S.TitleContainer>
										<Typography className="title" fontWeight="700" fullWidth noWrap>
											{item.shippingContract.name}
										</Typography>

										<S.SubtitleContainer>
											<Typography
												className="subtitle"
												color="var(--black-60)"
												fontWeight="500"
												fullWidth
												noWrap>
												{item.serviceType}
											</Typography>
											<Tag label={item.deliveryTime} />
										</S.SubtitleContainer>
									</S.TitleContainer>
								</S.LeftContentWrapper>
								<S.RightContentWrapper>
									<S.PriceContainer>
										{item.firstPrice > 0 && (
											<Typography
												className="first-price-text"
												variant="p2"
												color="var(--red)"
												style={{
													textDecoration: 'line-through'
												}}>{`${item.firstPrice.toFixed(2)} ${t(item.currency)}`}</Typography>
										)}
										<Typography className="price-text">{`${Number(getPackagePrice(item)).toFixed(2)} ${t(
											item.currency
										)}`}</Typography>
									</S.PriceContainer>
									<Button
										label="createShipment"
										size="xx-large"
										onClick={() => {
											window.parent.location.href = `https://app.tryoto.com/register/${language.code}`;
										}}
										fullWidth
									/>
								</S.RightContentWrapper>
							</S.PriceCard>
						))}
					</S.DataWrapper>
				</S.ResultContainer>
			)}
		</S.Container>
	);
};

export default PriceCalculatorWeb;
